import { Button, Modal, Select, Skeleton, Tabs } from '@mantine/core';
import {
  IconBroadcast,
  IconBuilding,
  IconCheck,
  IconFileInvoice,
  IconPlus,
  IconPointFilled,
  IconScale,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './Purchasing.module.css';
import { QuotationDialog } from './QuotationDialog.js';
import { RfqAwardsPanel } from './RfqAwards.js';
import { RfqStatusBadge } from './RfqList.js';
import { AddSupplierDialog, NewSupplierBadge } from './RfqMarket.js';
import {
  LineComparison,
  PortalStatus,
  PublishDialog,
  RevisionHistory,
  SourceBadge,
} from './RfqPortal.js';
import { formatAmount, formatQuantity, formatStampLocal } from './requisitions.js';
import type { RfqTab } from './rfqs.js';
import type { RfqSource } from './source.js';
import { LoadError } from './table.js';
import type { RfqDetail, RfqQuotation, Supplier } from './types.js';

type Dialog =
  | { kind: 'send' }
  | { kind: 'publish' }
  | { kind: 'cancel' }
  | { kind: 'invite' }
  | { kind: 'quote'; supplier: { id: string; name: string } }
  | { kind: 'select'; quotation: RfqQuotation }
  // Açık pazardaki aday tedarikçiyi şirket tedarikçisi yapma (ADR 0113 §4)
  | { kind: 'addSupplier'; supplier: { id: string; name: string } }
  | null;

/**
 * RFQ ve Supplier Comparison (`satin-alma-akisi.jpeg`; `satin-alma-modulu.md` §5–§6): RFQ
 * numarası ve durumu ("Sent to Suppliers"), "Created" günü; sekmeler RFQ Details (RFQ Items ve
 * Supplier Quotations, "Best Price" rozeti, "Compare & Select"), Quotations, Comparison
 * (kalem kalem fiyat, maliyet kalemleri, toplam teslim maliyeti, teslim süresi, geçerlilik,
 * garanti, tedarikçi puanı) ve Comments. Yalnız ofiste.
 */
export function RfqDetailView({
  source,
  id,
  tab,
  today,
  message,
  onTab,
  onBack,
  onOpenRequisition,
  onCreateOrder,
  onOpenOrder,
}: {
  source: RfqSource;
  id: string;
  tab: RfqTab;
  /** Ofisin bugünü: teklifin geliş günü varsayılanı. */
  today: string;
  message?: string | null;
  onTab: (tab: RfqTab) => void;
  onBack: () => void;
  onOpenRequisition?: (requisitionId: string) => void;
  /** Seçimden sipariş açar (ADR 0040 #179); hata kodu döner ya da `null`. */
  onCreateOrder?: (rfq: RfqDetail) => Promise<string | null>;
  onOpenOrder?: (purchaseOrderId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.detail.${key}`, options);
  const [detail, setDetail] = useState<RfqDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(message ?? null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setDetail(await source.get(id));
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('title')} />
        <LoadError title={k('error')} code={error} onRetry={() => void load()} />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className={classes.screen} aria-busy>
        <span className={classes.visuallyHidden}>{k('loading')}</span>
        <PageHeader title={k('title')} />
        {/* Düzenin şeklinde iskelet: başlık ve sekmeler, kalemler, bilgiler (M6f §0.6) */}
        <Skeleton height={64} />
        <Skeleton height={260} />
        <Skeleton height={160} />
      </div>
    );
  }

  const days = (value: number | null) => (value === null ? '—' : k('days', { count: value }));
  const money = (value: number | null) => formatAmount(value, locale);
  const run = async (action: () => Promise<RfqDetail>, done: (next: RfqDetail) => string) => {
    try {
      const next = await action();
      setDetail(next);
      setDialog(null);
      setNotice(done(next));
      return null;
    } catch (failure) {
      return errorCode(failure);
    }
  };
  const selected = detail.quotations.find((q) => q.id === detail.selectedQuotationId);
  // Satır bazında ödül (ADR 0111 §D): bölünebilir RFQ'da ya da ödülü varsa
  const awardsTab =
    source.awards !== undefined && (detail.can.award === true || (detail.awardCount ?? 0) > 0);
  // Karşılaştırmada birincil eylem: en iyi fiyatlı teklifi seç (küçük ekranda altta sabit)
  const best = detail.can.select
    ? detail.quotations.find((q) => q.bestPrice && q.eligible)
    : undefined;

  return (
    <div className={classes.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        crumb={detail.rfqNo}
        actions={
          <div className={classes.headerActions}>
            {detail.can.invite ? (
              <Button
                variant="default"
                leftSection={<IconPlus size={16} />}
                onClick={() => setDialog({ kind: 'invite' })}
              >
                {k('invite')}
              </Button>
            ) : null}
            {detail.can.cancel ? (
              <Button
                variant="default"
                leftSection={<IconX size={16} />}
                onClick={() => setDialog({ kind: 'cancel' })}
              >
                {k('cancel')}
              </Button>
            ) : null}
            {detail.order && onOpenOrder ? (
              <Button
                variant="default"
                leftSection={<IconFileInvoice size={16} />}
                onClick={() => onOpenOrder(detail.order?.id ?? '')}
              >
                {k('openOrder', { no: detail.order.poNo })}
              </Button>
            ) : null}
            {detail.can.order && onCreateOrder ? (
              <Button
                leftSection={<IconFileInvoice size={16} />}
                loading={ordering}
                onClick={async () => {
                  setOrdering(true);
                  const code = await onCreateOrder(detail);
                  setOrdering(false);
                  if (code) setOrderError(code);
                }}
              >
                {k('createPo')}
              </Button>
            ) : null}
            {detail.can.send ? (
              <Button
                variant={detail.can.publish && source.publish ? 'default' : 'filled'}
                leftSection={<IconSend size={16} />}
                onClick={() => setDialog({ kind: 'send' })}
              >
                {k('send')}
              </Button>
            ) : null}
            {detail.can.publish && source.publish ? (
              <Button
                leftSection={<IconBroadcast size={16} />}
                onClick={() => setDialog({ kind: 'publish' })}
              >
                {k('portal.publish')}
              </Button>
            ) : null}
          </div>
        }
      />

      {notice ? (
        <p className={classes.notice} data-tone="success" role="status">
          {notice}
        </p>
      ) : null}
      {orderError ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {t(`purchasing.errors.${orderError}`, { defaultValue: t('purchasing.errors.GENERIC') })}
        </p>
      ) : null}
      {selected ? (
        <p className={classes.notice} data-tone="info">
          {detail.selectionReason
            ? k('selectedNote', { supplier: selected.supplierName, reason: detail.selectionReason })
            : k('selectedNoReason', { supplier: selected.supplierName })}
        </p>
      ) : null}
      {detail.cancelledAt ? (
        <p className={classes.notice} data-tone="danger">
          {k('cancelledNote', { reason: detail.cancelReason ?? '' })}
        </p>
      ) : null}

      <Panel aria-label={k('title')}>
        <div className={classes.rfqHead}>
          <div className={classes.rfqTitle}>
            <h2>{detail.rfqNo}</h2>
            <RfqStatusBadge status={detail.status} />
          </div>
          <div className={classes.rfqMeta}>
            <span className={classes.created}>
              {k('created', { date: formatDay(detail.requestDate) })}
            </span>
            <PortalStatus detail={detail} now={new Date()} />
          </div>
        </div>
        <Tabs
          value={tab}
          onChange={(value) => onTab((value ?? 'details') as RfqTab)}
          className={classes.tabs}
          keepMounted={false}
        >
          <Tabs.List aria-label={k('tabsLabel')}>
            <Tabs.Tab value="details">{k('tabs.details')}</Tabs.Tab>
            <Tabs.Tab value="quotations">
              {k('tabs.quotations', { count: detail.quotations.length })}
            </Tabs.Tab>
            <Tabs.Tab value="comparison">{k('tabs.comparison')}</Tabs.Tab>
            <Tabs.Tab value="lines">{k('tabs.lines')}</Tabs.Tab>
            {awardsTab ? <Tabs.Tab value="award">{k('tabs.award')}</Tabs.Tab> : null}
            <Tabs.Tab value="comments">{k('tabs.comments')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details" className={classes.tabBody}>
            <h3 className={classes.subTitle}>{k('items')}</h3>
            <TableBox label={k('items')}>
              <thead>
                <tr>
                  <th scope="col" className={classes.index}>
                    {k('columns.no')}
                  </th>
                  <th scope="col" className={classes.key}>
                    {k('columns.material')}
                  </th>
                  <th scope="col" data-numeric>
                    {k('columns.qty')}
                  </th>
                  <th scope="col">{k('columns.uom')}</th>
                  <th scope="col">{k('columns.requiredDate')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line) => (
                  <tr key={line.id}>
                    <td className={classes.index}>{line.lineNo}</td>
                    <td className={`${classes.wrap} ${classes.key}`}>{line.name}</td>
                    <td data-numeric>{formatQuantity(line.quantity, locale)}</td>
                    <td>{line.unit}</td>
                    <td>{formatDay(line.requiredDate)}</td>
                  </tr>
                ))}
              </tbody>
            </TableBox>

            <h3 className={classes.subTitle}>{k('quotations')}</h3>
            {detail.quotations.length === 0 ? (
              <EmptyState title={k('noQuotations')} description={k('noQuotationsText')} />
            ) : (
              <TableBox label={k('quotations')}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.key}>
                      {k('columns.supplier')}
                    </th>
                    <th scope="col">{k('columns.totalPrice', { currency: detail.currency })}</th>
                    <th scope="col">{k('columns.leadTime')}</th>
                    <th scope="col">{k('columns.validity')}</th>
                    <th scope="col">{k('columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.quotations.map((q) => (
                    <tr key={q.id}>
                      <td className={classes.key}>
                        {q.supplierName}
                        {q.source === 'portal' ? (
                          <span className={classes.flags}>
                            <SourceBadge quotation={q} />
                            {q.newSupplier ? <NewSupplierBadge /> : null}
                          </span>
                        ) : null}
                      </td>
                      <td className={classes.strong}>
                        {money(q.landedTotal)}
                        {q.currency !== detail.currency ? ` ${q.currency}` : ''}
                      </td>
                      <td>{days(q.leadTimeDays)}</td>
                      <td>{days(q.validityDays)}</td>
                      <td>
                        <QuotationBadges quotation={q} selectedId={detail.selectedQuotationId} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableBox>
            )}
            <div className={classes.tabFooter}>
              <Button
                leftSection={<IconScale size={16} />}
                onClick={() => onTab('comparison')}
                disabled={detail.quotations.length === 0}
              >
                {k('compareSelect')}
              </Button>
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="quotations" className={classes.tabBody}>
            <h3 className={classes.subTitle}>{k('invited')}</h3>
            <TableBox label={k('invited')}>
              <thead>
                <tr>
                  <th scope="col" className={classes.key}>
                    {k('columns.supplier')}
                  </th>
                  <th scope="col">{k('columns.email')}</th>
                  <th scope="col">{k('columns.ref')}</th>
                  <th scope="col">{k('columns.received')}</th>
                  <th scope="col" data-numeric>
                    {k('columns.landed')}
                  </th>
                  <th scope="col">{k('columns.leadTime')}</th>
                  <th scope="col">{k('columns.status')}</th>
                  <th scope="col" data-numeric>
                    {k('columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ...detail.suppliers,
                  // "Açık" RFQ'da davetli olmayan tedarikçi de portaldan teklif verir (ADR 0111 §C)
                  ...detail.quotations
                    .filter((q) => !detail.suppliers.some((s) => s.id === q.supplierId))
                    .map((q) => ({ id: q.supplierId, name: q.supplierName, email: null })),
                ].map((supplier) => {
                  const q = detail.quotations.find((x) => x.supplierId === supplier.id);
                  const portal = q?.source === 'portal';
                  return (
                    <tr key={supplier.id}>
                      <td className={`${classes.wrap} ${classes.key}`}>{supplier.name}</td>
                      <td>{supplier.email ?? '—'}</td>
                      <td>{q?.quotationRef ?? '—'}</td>
                      <td>{q ? formatDay(q.receivedDate) : '—'}</td>
                      <td data-numeric>{q ? money(q.landedTotal) : '—'}</td>
                      <td>{q ? days(q.leadTimeDays) : '—'}</td>
                      <td>
                        {q ? (
                          <span className={classes.flags}>
                            <StatusBadge tone="success">{k('quoted')}</StatusBadge>
                            {portal ? <SourceBadge quotation={q} /> : null}
                            {q.newSupplier ? <NewSupplierBadge /> : null}
                            {q.late ? <StatusBadge tone="warning">{k('late')}</StatusBadge> : null}
                          </span>
                        ) : (
                          <StatusBadge tone="neutral">{k('notQuoted')}</StatusBadge>
                        )}
                      </td>
                      <td data-numeric>
                        {/* Portal teklifini yalnız tedarikçi yeni sürümle düzeltir (PORTAL_QUOTATION) */}
                        {q?.newSupplier && detail.can.invite && source.acceptSupplier ? (
                          <Button
                            size="xs"
                            variant="light"
                            aria-label={`${t('purchasingMarket.addSupplier')} · ${supplier.name}`}
                            onClick={() =>
                              setDialog({
                                kind: 'addSupplier',
                                supplier: { id: supplier.id, name: supplier.name },
                              })
                            }
                          >
                            {t('purchasingMarket.addSupplier')}
                          </Button>
                        ) : detail.can.quote && !portal ? (
                          <Button
                            size="xs"
                            variant="light"
                            aria-label={`${k(q ? 'editQuotation' : 'enterQuotation')} · ${supplier.name}`}
                            onClick={() =>
                              setDialog({
                                kind: 'quote',
                                supplier: { id: supplier.id, name: supplier.name },
                              })
                            }
                          >
                            {k(q ? 'editQuotation' : 'enterQuotation')}
                          </Button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TableBox>
            {detail.quotations.map((q) => (
              <RevisionHistory key={q.id} quotation={q} money={money} />
            ))}
          </Tabs.Panel>

          <Tabs.Panel value="comparison" className={classes.tabBody}>
            {detail.quotations.length === 0 ? (
              <EmptyState title={k('comparisonEmpty')} />
            ) : (
              <>
                <p className={classes.note}>{k('comparisonNote', { currency: detail.currency })}</p>
                <Comparison
                  detail={detail}
                  money={money}
                  days={days}
                  quantity={(value) => formatQuantity(value, locale)}
                  onSelect={(quotation) => setDialog({ kind: 'select', quotation })}
                />
                {best ? (
                  <div
                    className={classes.mobileActions}
                    role="toolbar"
                    aria-label={t('purchasing.table.actions')}
                  >
                    <Button onClick={() => setDialog({ kind: 'select', quotation: best })}>
                      {k('selectOf', { supplier: best.supplierName })}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="lines" className={classes.tabBody}>
            <LineComparison
              detail={detail}
              money={money}
              quantity={(value) => formatQuantity(value, locale)}
            />
          </Tabs.Panel>

          {awardsTab && source.awards ? (
            <Tabs.Panel value="award" className={classes.tabBody}>
              <RfqAwardsPanel
                detail={detail}
                source={source.awards}
                money={money}
                quantity={(value) => formatQuantity(value, locale)}
                {...(onOpenOrder ? { onOpenOrder } : {})}
                onChanged={() => void load()}
              />
            </Tabs.Panel>
          ) : null}

          <Tabs.Panel value="comments" className={classes.tabBody}>
            {detail.events.length === 0 ? (
              <EmptyState title={k('noComments')} />
            ) : (
              <ul className={classes.comments}>
                {detail.events.map((event) => (
                  <li
                    key={`${event.kind}-${event.at}-${event.supplierName ?? ''}`}
                    className={classes.comment}
                  >
                    <span className={classes.avatar} aria-hidden>
                      <IconBuilding size={16} />
                    </span>
                    <div>
                      <p className={classes.commentRole}>
                        {k(`events.${event.kind}`, { supplier: event.supplierName ?? '' })}
                      </p>
                      <p className={classes.commentText}>
                        {[event.byName, event.reason].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </div>
                    <span className={classes.commentAt}>{formatStampLocal(event.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Tabs.Panel>
        </Tabs>
      </Panel>

      <Panel title={k('info')} flush>
        <dl className={classes.facts}>
          <Fact label={k('requisition')}>
            {onOpenRequisition ? (
              <a
                className={classes.link}
                href={`?id=${detail.requisition.id}`}
                aria-label={k('openRequisition', { no: detail.requisition.requisitionNo })}
                onClick={(event) => {
                  event.preventDefault();
                  onOpenRequisition(detail.requisition.id);
                }}
              >
                {detail.requisition.requisitionNo}
              </a>
            ) : (
              detail.requisition.requisitionNo
            )}
          </Fact>
          <Fact label={k('vessel')}>{detail.vesselName}</Fact>
          <Fact label={k('closingDate')}>{formatDay(detail.closingDate)}</Fact>
          <Fact label={k('currency')}>{detail.currency}</Fact>
          <Fact label={k('deliveryLocation')}>{detail.deliveryLocation ?? '—'}</Fact>
          <Fact label={k('validity')}>{days(detail.validityDays)}</Fact>
          <Fact label={k('paymentTerms')}>{detail.paymentTerms ?? '—'}</Fact>
          <Fact label={k('warranty')}>{detail.warranty ?? '—'}</Fact>
          <Fact label={k('deliveryRequirement')}>{detail.deliveryRequirement ?? '—'}</Fact>
          <Fact label={k('technicalSpec')}>{detail.technicalSpec ?? '—'}</Fact>
        </dl>
      </Panel>

      <div>
        <Button variant="subtle" onClick={onBack}>
          {k('back')}
        </Button>
      </div>

      <PasswordDialog
        opened={dialog?.kind === 'send'}
        title={k('sendTitle')}
        body={k('sendBody')}
        reason="none"
        password={false}
        confirmLabel={k('send')}
        onClose={() => setDialog(null)}
        onConfirm={() =>
          run(
            () => source.send(detail.id, detail.rowVersion),
            (next) => k('notices.sent', { no: next.rfqNo }),
          )
        }
      />
      <PasswordDialog
        opened={dialog?.kind === 'cancel'}
        title={k('cancelTitle')}
        body={k('cancelBody')}
        reason="required"
        password={false}
        confirmLabel={k('cancel')}
        onClose={() => setDialog(null)}
        onConfirm={({ reason }) =>
          run(
            () => source.cancel(detail.id, { rowVersion: detail.rowVersion, reason }),
            (next) => k('notices.cancelled', { no: next.rfqNo }),
          )
        }
      />
      {dialog?.kind === 'select' ? (
        <PasswordDialog
          opened
          title={k('selectTitle', { supplier: dialog.quotation.supplierName })}
          body={
            dialog.quotation.bestPrice
              ? k('selectBody')
              : `${k('selectBody')} ${k('selectReasonBody')}`
          }
          reason={dialog.quotation.bestPrice ? 'optional' : 'required'}
          reasonLabel={k('selectReason')}
          password={false}
          confirmLabel={k('select')}
          onClose={() => setDialog(null)}
          onConfirm={({ reason }) =>
            run(
              () =>
                source.select(detail.id, {
                  rowVersion: detail.rowVersion,
                  quotationId: dialog.quotation.id,
                  reason: reason || null,
                }),
              () => k('notices.selected', { supplier: dialog.quotation.supplierName }),
            )
          }
        />
      ) : null}
      {dialog?.kind === 'quote' ? (
        <QuotationDialog
          rfq={detail}
          supplier={dialog.supplier}
          today={today}
          source={source}
          onClose={() => setDialog(null)}
          onSaved={(next) => {
            setDetail(next);
            setDialog(null);
            setNotice(k('notices.quoted', { supplier: dialog.supplier.name }));
          }}
        />
      ) : null}
      {dialog?.kind === 'publish' ? (
        <PublishDialog
          detail={detail}
          source={source}
          now={new Date()}
          onClose={() => setDialog(null)}
          onPublished={(next) => {
            setDetail(next);
            setDialog(null);
            setNotice(k('notices.published', { no: next.rfqNo }));
          }}
        />
      ) : null}
      {dialog?.kind === 'addSupplier' && source.acceptSupplier ? (
        <AddSupplierDialog
          name={dialog.supplier.name}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await source.acceptSupplier?.(dialog.supplier.id);
            setDetail(await source.get(detail.id));
            setDialog(null);
            setNotice(t('purchasingMarket.added', { name: dialog.supplier.name }));
          }}
        />
      ) : null}
      {dialog?.kind === 'invite' ? (
        <InviteDialog
          detail={detail}
          source={source}
          onClose={() => setDialog(null)}
          onInvited={(next, name) => {
            setDetail(next);
            setDialog(null);
            setNotice(k('notices.invited', { supplier: name }));
          }}
        />
      ) : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function TableBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={`${table.tableWrap} ${classes.boxed}`}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table}`}>{children}</table>
    </section>
  );
}

function QuotationBadges({
  quotation,
  selectedId,
  empty = '-',
}: {
  quotation: RfqQuotation;
  selectedId: string | null;
  /** Rozet yoksa gösterilen (tasarımdaki tabloda "-"). */
  empty?: string | null;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`purchasing.rfq.detail.${key}`);
  const badges: ReactNode[] = [];
  if (quotation.id === selectedId) {
    badges.push(
      <StatusBadge key="selected" tone="info" icon={<IconCheck size={10} />}>
        {k('selected')}
      </StatusBadge>,
    );
  }
  if (quotation.bestPrice) {
    badges.push(
      <StatusBadge key="best" tone="success" icon={<IconPointFilled size={10} />}>
        {k('bestPrice')}
      </StatusBadge>,
    );
  }
  if (quotation.reason) {
    badges.push(
      <StatusBadge key="excluded" tone="warning">
        {t(`purchasing.rfq.detail.excluded.${quotation.reason}`)}
      </StatusBadge>,
    );
  }
  return badges.length ? <span className={classes.flags}>{badges}</span> : <>{empty}</>;
}

/**
 * Karşılaştırma tablosu (`satin-alma-modulu.md` §6): sütunlar teklifler (toplam maliyet sırası),
 * satırlar kalemlerin birim fiyatı, maliyet kalemleri, toplam teslim maliyeti, teslim süresi,
 * geçerlilik, garanti ve tedarikçi puanı. Uygun teklif seçilebilir (sorular #78).
 */
function Comparison({
  detail,
  money,
  days,
  quantity,
  onSelect,
}: {
  detail: RfqDetail;
  money: (value: number | null) => string;
  days: (value: number | null) => string;
  quantity: (value: number) => string;
  onSelect: (quotation: RfqQuotation) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.detail.${key}`, options);
  const quotations = detail.quotations;
  const row = (label: string, cell: (q: RfqQuotation) => ReactNode, strong = false) => (
    <tr data-strong={strong || undefined}>
      <th scope="row" className={classes.key}>
        {label}
      </th>
      {quotations.map((q) => (
        <td key={q.id} data-numeric data-best={q.bestPrice || undefined}>
          {cell(q)}
        </td>
      ))}
    </tr>
  );
  return (
    <section
      className={`${table.tableWrap} ${classes.boxed}`}
      aria-label={k('tabs.comparison')}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.table} ${classes.compare}`}>
        <thead>
          <tr>
            <th scope="col" className={classes.key}>
              {k('columns.material')}
            </th>
            {quotations.map((q) => (
              <th key={q.id} scope="col" data-numeric data-best={q.bestPrice || undefined}>
                <span className={classes.compareHead}>
                  {q.supplierName}
                  <QuotationBadges
                    quotation={q}
                    selectedId={detail.selectedQuotationId}
                    empty={null}
                  />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.lines.map((line) => (
            <tr key={line.id}>
              <th scope="row" className={classes.key}>
                {line.lineNo}. {line.name}
                <span className={classes.muted}>
                  {' '}
                  · {quantity(line.quantity)} {line.unit}
                </span>
              </th>
              {quotations.map((q) => {
                const price = q.lines.find((l) => l.rfqLineId === line.id);
                return (
                  <td key={q.id} data-numeric data-best={q.bestPrice || undefined}>
                    {price?.unitPrice === null || price === undefined
                      ? '—'
                      : `${money(price.unitPrice)} × ${quantity(line.quantity)} = ${money(price.total)}`}
                  </td>
                );
              })}
            </tr>
          ))}
          {row(k('columns.items'), (q) => money(q.itemsTotal))}
          {row(k('columns.discount'), (q) => (q.discount ? `−${money(q.discount)}` : '—'))}
          {row(k('columns.freight'), (q) => (q.freight ? money(q.freight) : '—'))}
          {row(k('columns.customs'), (q) => (q.customs ? money(q.customs) : '—'))}
          {row(k('columns.tax'), (q) => (q.tax ? money(q.tax) : '—'))}
          {row(k('columns.serviceFee'), (q) => (q.serviceFee ? money(q.serviceFee) : '—'))}
          {row(
            `${k('columns.landed')} (${detail.currency})`,
            (q) =>
              `${money(q.landedTotal)}${q.currency !== detail.currency ? ` ${q.currency}` : ''}`,
            true,
          )}
          {row(k('columns.leadTime'), (q) => days(q.leadTimeDays))}
          {row(k('columns.validity'), (q) => days(q.validityDays))}
          {row(k('columns.warranty'), (q) => q.warranty ?? '—')}
          {row(k('columns.rating'), (q) => (q.supplierRating ? `${q.supplierRating} / 5` : '—'))}
          {detail.can.select ? (
            <tr>
              <th scope="row" className={classes.key}>
                <span className={classes.visuallyHidden}>{k('columns.actions')}</span>
              </th>
              {quotations.map((q) => (
                <td key={q.id} data-numeric>
                  {q.eligible ? (
                    <Button
                      size="xs"
                      variant={q.bestPrice ? 'filled' : 'light'}
                      aria-label={k('selectOf', { supplier: q.supplierName })}
                      onClick={() => onSelect(q)}
                    >
                      {k('select')}
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              ))}
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}

function InviteDialog({
  detail,
  source,
  onClose,
  onInvited,
}: {
  detail: RfqDetail;
  source: RfqSource;
  onClose: () => void;
  onInvited: (next: RfqDetail, supplierName: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`purchasing.rfq.detail.${key}`);
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    source
      .suppliers()
      .then((list) => {
        const invited = new Set(detail.suppliers.map((s) => s.id));
        if (alive) {
          setSuppliers(list.items.filter((s) => s.approved && s.active && !invited.has(s.id)));
        }
      })
      .catch((failure) => {
        if (alive) {
          setSuppliers([]);
          setError(errorCode(failure));
        }
      });
    return () => {
      alive = false;
    };
  }, [source, detail.suppliers]);
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('inviteTitle')}
      closeButtonProps={{ 'aria-label': t('purchasing.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!choice) return;
          setBusy(true);
          try {
            const name = suppliers?.find((s) => s.id === choice)?.name ?? '';
            onInvited(await source.invite(detail.id, choice), name);
          } catch (failure) {
            setError(errorCode(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={classes.muted}>{k('inviteBody')}</p>
        {suppliers === null ? (
          <Skeleton height={36} />
        ) : (
          <Select
            label={k('inviteSupplier')}
            searchable
            data={suppliers.map((s) => ({ value: s.id, label: s.name }))}
            value={choice}
            onChange={setChoice}
            mb="sm"
            data-autofocus
          />
        )}
        {error ? (
          <p className={classes.error} role="alert">
            {t(`purchasing.errors.${error}`, { defaultValue: t('purchasing.errors.GENERIC') })}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.password.back')}
          </Button>
          <Button type="submit" loading={busy} disabled={!choice}>
            {k('inviteConfirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
